import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createPopper } from "@popperjs/core";

/**
 * DropdownPortal component that renders a dropdown menu using React Portal
 *
 * Features:
 * - Supports both controlled and uncontrolled state
 * - Automatic positioning using Popper.js
 * - Click outside to close functionality
 * - High z-index for proper layering
 * - Flexible state management for different use cases
 *
 * @param button - The trigger button element
 * @param children - The dropdown menu content
 * @param isOpen - Optional controlled state for dropdown visibility
 * @param onToggle - Optional callback for state changes
 */
type Props = {
  button: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean; // Controlled state for dropdown visibility
  onToggle?: (isOpen: boolean) => void; // Callback for state changes
};

const DropdownPortal: React.FC<Props> = ({
  button,
  children,
  isOpen: controlledIsOpen,
  onToggle,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  // This allows the component to be used both as controlled and uncontrolled
  const open = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (controlledIsOpen !== undefined) {
      onToggle?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Create popper instance for positioning
  React.useEffect(() => {
    let popperInstance: any;
    if (open && buttonRef.current && menuRef.current) {
      popperInstance = createPopper(buttonRef.current, menuRef.current, {
        placement: "bottom-end",
        modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
      });
    }
    return () => {
      if (popperInstance) popperInstance.destroy();
    };
  }, [open]);

  // Handle clicks outside to close dropdown
  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  return (
    <>
      {React.cloneElement(button as React.ReactElement, {
        ref: buttonRef,
        onClick: () => setOpen(!open),
      })}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              zIndex: 9999,
              minWidth: 160,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              padding: 8,
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
};

export default DropdownPortal;
